import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download, Printer } from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/ui";

const usd = (n) => (n ? `$${Math.round(n).toLocaleString()}` : "—");
const int = (n) => (n || n === 0 ? Math.round(n).toLocaleString() : "—");
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const ROWS = [
  ["Estimated players reached", (d) => int(d.players)],
  ["Teams", (d) => int(d.teams)],
  ["Coaching places", (d) => int(d.coaching_places)],
  ["Facility units proposed", (d) => int(d.facility_units)],
  ["Land needed, acres", (d) => `${d.land_acres}`],
  ["Capex", (d) => usd(d.capex_usd)],
  ["Opex a year", (d) => usd(d.opex_usd)],
  ["Potential revenue a year", (d) => usd(d.annual_revenue_usd)],
  ["Operating surplus a year", (d) => usd(d.operating_surplus_usd)],
  ["Payback period", (d) => (d.payback_years ? `${d.payback_years} years` : "Needs blended funding")],
  ["Economic and social value a year", (d) => usd(d.annual_value_usd)],
  ["Value per dollar spent", (d) => `${d.value_per_dollar}`],
  ["Health value a year", (d) => usd(d.health_value_usd)],
  ["Tax a year", (d) => usd(d.tax_usd)],
  ["Jobs", (d) => int(d.operational_jobs)],
  ["Academy places", (d) => int(d.academy_places)],
  ["Professionals produced a year", (d) => (d.pros_per_year ?? "—")],
  ["Player trading income a year", (d) => usd(d.programme_income_usd)],
];

/** Two county one-pagers side by side, on one printable sheet. */
export default function CountyCompareSheet() {
  const [params, setParams] = useSearchParams();
  const a = params.get("a") || "Nairobi";
  const b = params.get("b") || "Kisumu";
  const [counties, setCounties] = useState([]);
  const [left, setLeft] = useState(null);
  const [right, setRight] = useState(null);
  const [err, setErr] = useState("");

  useSeo({
    title: `${a} and ${b} side by side — KickStartKenyaFootball`,
    description:
      "Two Kenyan counties on one printable sheet: estimated players reached, facilities, land, capex, opex, potential revenue and the returns alongside them.",
  });

  useEffect(() => {
    api.get("/priority-counties").then((r) => setCounties(r.data.rows)).catch(() => setCounties([]));
  }, []);

  useEffect(() => {
    setErr("");
    Promise.all([api.get(`/county-one-pager/${slugify(a)}`), api.get(`/county-one-pager/${slugify(b)}`)])
      .then(([x, y]) => { setLeft(x.data); setRight(y.data); })
      .catch(() => setErr("We could not load those two counties."));
  }, [a, b]);

  const pick = (key) => (e) => {
    const next = new URLSearchParams(params);
    next.set(key, e.target.value);
    setParams(next, { replace: true });
  };

  const winner = useMemo(() => {
    if (!left || !right) return null;
    if (left.county === right.county) return null;
    return left.value_per_dollar >= right.value_per_dollar ? left.county : right.county;
  }, [left, right]);

  const select = "mt-2 border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none";

  if (err)
    return (
      <div className="bg-black px-5 py-24 text-center" data-testid="compare-sheet-error">
        <h1 className="display-3">{err}</h1>
        <Link to="/counties" className="btn-primary mt-8 inline-flex">Back to the county map</Link>
      </div>
    );

  return (
    <div className="bg-black pb-24 pt-10 print:bg-white print:pt-0" data-testid="county-compare-sheet">
      <div className="mx-auto max-w-[1100px] px-5 md:px-10 print:max-w-none print:px-0 print:text-black">
        <p className="mono text-[0.6rem] uppercase tracking-[0.2em] text-ochre">Comparison sheet</p>
        <h1 className="mt-3 font-display text-3xl font-bold uppercase leading-none tracking-tight text-white print:text-black md:text-5xl"
          data-testid="compare-sheet-title">
          {a} and {b}, Side By Side
        </h1>

        <div className="mt-7 flex flex-wrap items-end gap-4 print:hidden">
          <label className="block">
            <span className="mono block text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">First county</span>
            <select value={a} onChange={pick("a")} className={select} data-testid="compare-sheet-a">
              {counties.map((c) => <option key={c.county} value={c.county}>{`${c.county} — #${c.rank}`}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mono block text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">Second county</span>
            <select value={b} onChange={pick("b")} className={select} data-testid="compare-sheet-b">
              {counties.map((c) => <option key={c.county} value={c.county}>{`${c.county} — #${c.rank}`}</option>)}
            </select>
          </label>
          <button type="button" onClick={() => window.print()} className="btn-primary mb-0.5 inline-flex items-center gap-2"
            data-testid="compare-sheet-print">
            <Printer size={14} /> Print this sheet
          </button>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/county-compare-sheet.pdf?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`}
            className="btn-ghost mb-0.5 inline-flex items-center gap-2" data-testid="compare-sheet-pdf">
            <Download size={14} /> Download the PDF
          </a>
        </div>

        {left && right ? (
          <>
            <div className="mt-8 overflow-x-auto border border-zinc-800 print:border-zinc-400">
              <table className="w-full min-w-[640px] text-left" data-testid="compare-sheet-table">
                <thead>
                  <tr className="border-b border-zinc-800 bg-[#0b0b0b] print:bg-white">
                    <th className="mono px-3 py-2.5 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-300 print:text-zinc-700">
                      Measure
                    </th>
                    {[left, right].map((d, i) => (
                      <th key={`${i}-${d.county}`} className="mono px-3 py-2.5 text-[0.55rem] uppercase tracking-[0.14em] text-ochre print:text-black">
                        {d.county}
                        {d.priority ? ` · #${d.priority_rank}` : ""}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-zinc-900 print:border-zinc-300">
                    <td className="px-3 py-3 text-sm text-white print:text-black">Population today</td>
                    <td className="mono px-3 py-3 text-xs text-zinc-300 print:text-zinc-700">{int(left.population_today)}</td>
                    <td className="mono px-3 py-3 text-xs text-zinc-300 print:text-zinc-700">{int(right.population_today)}</td>
                  </tr>
                  {ROWS.map(([label, read]) => (
                    <tr key={label} className="border-b border-zinc-900 print:border-zinc-300"
                      data-testid={`compare-sheet-row-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <td className="px-3 py-3 text-sm text-white print:text-black">{label}</td>
                      <td className="mono px-3 py-3 text-xs text-zinc-300 print:text-zinc-700">{read(left)}</td>
                      <td className="mono px-3 py-3 text-xs text-zinc-300 print:text-zinc-700">{read(right)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-zinc-400 print:text-zinc-700" data-testid="compare-sheet-verdict">
              {winner ? (
                <>
                  On value returned per dollar built, <span className="text-white print:text-black">{winner}</span> works
                  harder on these estimates.{" "}
                </>
              ) : (
                <>The same county is selected on both sides, so there is nothing to separate. </>
              )}
              Both sets of figures are research estimates: capex and opex answer to a
              survey and a detailed costing, land to a registered valuer.
            </p>
            <div className="mono mt-6 flex flex-wrap gap-4 text-[0.6rem] uppercase tracking-[0.16em] print:hidden">
              <Link to={`/counties/${slugify(a)}/one-page`} className="text-ochre hover:text-white" data-testid="compare-sheet-to-a">
                {a} on one page
              </Link>
              <Link to={`/counties/${slugify(b)}/one-page`} className="text-ochre hover:text-white" data-testid="compare-sheet-to-b">
                {b} on one page
              </Link>
              <Link to="/compare" className="text-ochre hover:text-white" data-testid="compare-sheet-to-compare">
                Full county comparison
              </Link>
            </div>
          </>
        ) : (
          <div className="mt-10 h-40" data-testid="compare-sheet-loading" />
        )}
      </div>
    </div>
  );
}
